import { FaUsers, FaStar, FaClock, FaTag, FaGift, FaLock, FaBoxOpen, FaFire } from "react-icons/fa";
import { getTierTypeBadge, formatPrice } from "../../services/ticketingService";
import "./TierSelector.css";

/**
 * Renders ticket tier cards for an event.
 *
 * Early bird / GA: mutually exclusive based on timing.
 * Member tiers: never shown as separate cards — member price is shown
 * inline on each regular tier card when the user is a member.
 *
 * Props:
 *  - tiers: array of tier objects (with sale_status + member_locked from API)
 *  - quantities: { [tier_id]: number }
 *  - onQuantityChange: (tier_id, newQty) => void
 *  - promotions: array of active promotions
 *  - onLoginPrompt: () => void
 */
export default function TierSelector({ tiers, quantities, onQuantityChange, onLoginPrompt, promotions, packs, packQuantities, onPackQuantityChange, eventCapacityRemaining }) {
  if (!tiers || tiers.length === 0) {
    return (
      <div className="tier-selector-empty">
        <FaClock className="tier-empty-icon" />
        <p>No tickets available at this time.</p>
        <span>Check back closer to the event date.</span>
      </div>
    );
  }

  // ── Member tier resolution ────────────────────────────────────────────────
  // member_locked=false means this user IS a member and can purchase it
  const memberTier = tiers.find((t) => t.is_member_only && !t.member_locked);
  const anyMemberTier = tiers.find((t) => t.is_member_only); // for teaser even if locked
  const isMember = !!memberTier;

  // Compute what a member would pay for a given regular tier's price
  const computeMemberPrice = (regularPriceCents) => {
    if (!anyMemberTier) return null;
    const dt = anyMemberTier.member_discount_type;
    const dv = anyMemberTier.member_discount_value || 0;
    if (dt === "set_price") return anyMemberTier.price_cents;
    if (dt === "percent_off") return Math.max(0, Math.round(regularPriceCents * (1 - dv / 100)));
    if (dt === "amount_off") return Math.max(0, regularPriceCents - dv);
    return null;
  };

  // ── Tier type helpers ─────────────────────────────────────────────────────
  // Early bird = has sale_ends_at but NO sale_starts_at
  // Door       = has sale_starts_at (goes on sale on event day)
  // GA         = neither
  const now = new Date();
  const regularTiers = tiers.filter((t) => !t.is_member_only);
  const isEarlyBirdTier = (t) => !!t.sale_ends_at && !t.sale_starts_at;
  const isDoorTier = (t) => !!t.sale_starts_at && !t.sale_ends_at;

  // Door is active when its sale window has opened (sale_starts_at <= now)
  const hasActiveDoor = regularTiers.some(
    (t) => isDoorTier(t) && (t.sale_status === "on_sale")
  );

  const hasActiveEarlyBird = regularTiers.some(
    (t) => isEarlyBirdTier(t) && new Date(t.sale_ends_at) > now && t.price_cents > 0
  );

  // Priority: Door (event day) > Early Bird > GA
  const displayTiers = regularTiers.filter((t) => {
    if (t.price_cents === 0) return true; // always show free
    if (hasActiveDoor) {
      // Event day: show only door tiers that are currently on sale
      return isDoorTier(t) && t.sale_status === "on_sale";
    }
    if (hasActiveEarlyBird) {
      // Early bird period: show only active early bird tiers
      return isEarlyBirdTier(t) && new Date(t.sale_ends_at) > now;
    }
    // GA period: show non-door, non-ended-early-bird tiers
    return !isDoorTier(t) && (!isEarlyBirdTier(t) || new Date(t.sale_ends_at) > now);
  });

  const formatSaleDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
      timeZone: "America/Chicago",
    });
  };

  const getTierIcon = (badge) => {
    if (!badge) return <FaTag />;
    switch (badge.color) {
      case "early-bird": return <FaClock />;
      case "member":     return <FaStar />;
      case "free":       return <FaGift />;
      default:           return <FaTag />;
    }
  };

  return (
    <div className="tier-selector">
      {/* Event-level capacity warning */}
      {eventCapacityRemaining !== null && eventCapacityRemaining !== undefined && eventCapacityRemaining <= 5 && (
        <div className="tier-event-capacity-warning">
          <FaFire />
          <span>
            Almost sold out — only {eventCapacityRemaining} ticket{eventCapacityRemaining !== 1 ? "s" : ""} left!
          </span>
        </div>
      )}

      {/* Promotion banners */}
      {promotions && promotions.length > 0 && (
        <div className="promo-banners">
          {promotions.map((promo) => {
            const tierName = promo.applies_to_tier_id
              ? (tiers.find((t) => t.tier_id === promo.applies_to_tier_id)?.name || "selected tickets")
              : "any tickets";
            return (
              <div key={promo.promo_id} className="promo-banner">
                <FaGift className="promo-banner-icon" />
                <div>
                  <strong>{promo.name}</strong>
                  <span className="promo-banner-desc">
                    Buy {promo.buy_qty} {tierName}, get {promo.free_qty} free
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(() => {
        // Total tickets already in cart across all tiers + packs — used to enforce event capacity
        const totalTierQty = Object.values(quantities).reduce((s, q) => s + q, 0);
        const totalPackQty = (packs || []).reduce(
          (s, p) => s + (packQuantities?.[p.pack_id] || 0) * p.quantity, 0
        );
        const totalInCart = totalTierQty + totalPackQty;

        return displayTiers.map((tier) => {
        const badge = getTierTypeBadge(tier);
        const saleStatus = tier.sale_status || "on_sale";
        const isSoldOut = tier.tickets_available !== null && tier.tickets_available <= 0;
        const isOnSale = saleStatus === "on_sale" && !isSoldOut;
        const isUpcoming = saleStatus === "upcoming";
        const isEnded = saleStatus === "ended";

        // When user is a member, cart uses member tier's ID so they get member pricing
        const cartTierId = isMember && memberTier ? memberTier.tier_id : tier.tier_id;
        const qty = quantities[cartTierId] || 0;
        // Cap by per-tier remaining, event-level space, and UI hard limit of 10
        const eventAllowance = eventCapacityRemaining != null
          ? Math.max(0, eventCapacityRemaining - (totalInCart - qty))
          : Infinity;
        const maxQty = Math.min(
          tier.tickets_available !== null ? tier.tickets_available : 10,
          eventAllowance,
          10
        );

        const saleEndsAt = tier.sale_ends_at ? new Date(tier.sale_ends_at) : null;
        const hoursLeft = saleEndsAt && isOnSale
          ? Math.max(0, Math.round((saleEndsAt - Date.now()) / (1000 * 60 * 60)))
          : null;

        // Member price for this specific tier
        const memberPrice = tier.price_cents > 0 ? computeMemberPrice(tier.price_cents) : null;
        const showMemberPrice = memberPrice !== null && memberPrice < tier.price_cents;

        return (
          <div
            key={tier.tier_id}
            className={[
              "tier-card",
              qty > 0 ? "tier-card--selected" : "",
              isSoldOut ? "tier-card--sold-out" : "",
              (isUpcoming || isEnded) ? "tier-card--unavailable" : "",
              badge ? `tier-card--${badge.color}` : "",
            ].filter(Boolean).join(" ")}
          >
            {badge && (
              <div className={`tier-badge tier-badge--${badge.color}`}>
                {getTierIcon(badge)}<span>{badge.label}</span>
              </div>
            )}

            {badge?.color === "early-bird" && isOnSale && hoursLeft !== null && hoursLeft < 48 && (
              <div className="tier-urgency">
                <FaClock />
                <span>{hoursLeft < 1 ? "Ends in less than an hour!" : `Ends in ${hoursLeft}h`}</span>
              </div>
            )}

            <div className="tier-card-body">
              <div className="tier-info">
                <h3 className="tier-name">{tier.name}</h3>
                {tier.description && <p className="tier-description">{tier.description}</p>}

                {isUpcoming && (
                  <div className="tier-sale-status tier-sale-status--upcoming">
                    <FaClock /><span>On sale {formatSaleDate(tier.sale_starts_at)}</span>
                  </div>
                )}
                {isEnded && (
                  <div className="tier-sale-status tier-sale-status--ended">
                    <FaLock /><span>Sale ended</span>
                  </div>
                )}

                {/* Non-member teaser — show actual member price so the value is clear */}
                {!isMember && anyMemberTier && isOnSale && tier.price_cents > 0 && (() => {
                  const mp = computeMemberPrice(tier.price_cents);
                  if (mp === null || mp >= tier.price_cents) return null;
                  return (
                    <button className="tier-member-teaser" onClick={onLoginPrompt}>
                      <FaStar />
                      <span>Members pay <strong>{formatPrice(mp)}</strong> — Sign in</span>
                    </button>
                  );
                })()}

                {isOnSale && tier.tickets_available !== null && (
                  <div className={`tier-availability ${tier.tickets_available <= 10 ? "tier-availability--low" : ""}`}>
                    <FaUsers />
                    <span>
                      {tier.tickets_available <= 10
                        ? `Only ${tier.tickets_available} left!`
                        : `${tier.tickets_available} available`}
                    </span>
                  </div>
                )}
                {isSoldOut && (
                  <div className="tier-availability tier-availability--sold-out">
                    <span>Sold out</span>
                  </div>
                )}
              </div>

              <div className="tier-right">
                {/* Price — show member discount inline when logged in */}
                {isMember && showMemberPrice ? (
                  <div className="tier-price-group">
                    <span className="tier-price--original">{formatPrice(tier.price_cents)}</span>
                    <span className="tier-price tier-price--member">{formatPrice(memberPrice)}</span>
                    <span className="tier-savings-label">Member Price</span>
                  </div>
                ) : (
                  <div className="tier-price">{formatPrice(tier.price_cents)}</div>
                )}

                {isOnSale && (
                  <div className="tier-quantity-stepper">
                    <button
                      className="stepper-btn"
                      onClick={() => onQuantityChange(cartTierId, Math.max(0, qty - 1))}
                      disabled={qty === 0}
                      aria-label="Decrease quantity"
                    >−</button>
                    <span className="stepper-count">{qty}</span>
                    <button
                      className="stepper-btn"
                      onClick={() => onQuantityChange(cartTierId, Math.min(maxQty, qty + 1))}
                      disabled={qty >= maxQty}
                      aria-label="Increase quantity"
                    >+</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
        });
      })()}

      {/* Ticket pack cards */}
      {packs && packs.filter((p) => p.sale_status !== "ended").length > 0 && (
        <div className="tier-packs-section">
          <div className="tier-packs-header">
            <FaBoxOpen className="tier-packs-header-icon" />
            <span>Bundle Deals</span>
          </div>
          {packs.filter((p) => p.sale_status !== "ended").map((pack) => {
            const isSoldOut = pack.packs_available !== null && pack.packs_available <= 0;
            const isOnSale = pack.sale_status === "on_sale" && !isSoldOut;
            const isUpcoming = pack.sale_status === "upcoming";
            const qty = (packQuantities || {})[pack.pack_id] || 0;
            const packTicketsInCart = qty * pack.quantity;
            const totalInCartForPack = Object.values(quantities).reduce((s, q) => s + q, 0)
              + (packs || []).reduce((s, p) => s + (packQuantities?.[p.pack_id] || 0) * p.quantity, 0);
            const eventAllowanceForPack = eventCapacityRemaining != null
              ? Math.max(0, Math.floor(
                  (eventCapacityRemaining - (totalInCartForPack - packTicketsInCart)) / pack.quantity
                ))
              : Infinity;
            const maxQty = Math.min(
              pack.packs_available !== null ? pack.packs_available : 10,
              eventAllowanceForPack,
              10
            );
            const perTicketCents = Math.round(pack.pack_price_cents / pack.quantity);

            return (
              <div
                key={pack.pack_id}
                className={[
                  "tier-card tier-card--pack",
                  qty > 0 ? "tier-card--selected" : "",
                  isSoldOut ? "tier-card--sold-out" : "",
                  isUpcoming ? "tier-card--unavailable" : "",
                ].filter(Boolean).join(" ")}
              >
                <div className="tier-badge tier-badge--pack">
                  <FaBoxOpen /><span>Bundle</span>
                </div>

                <div className="tier-card-body">
                  <div className="tier-info">
                    <h3 className="tier-name">{pack.name}</h3>
                    {pack.description && <p className="tier-description">{pack.description}</p>}
                    <p className="tier-pack-quantity">
                      <FaUsers /> {pack.quantity} tickets per bundle
                    </p>

                    {isUpcoming && (
                      <div className="tier-sale-status tier-sale-status--upcoming">
                        <FaClock /><span>On sale {formatSaleDate(pack.sale_starts_at)}</span>
                      </div>
                    )}

                    {isOnSale && pack.packs_available !== null && (
                      <div className={`tier-availability ${pack.packs_available <= 5 ? "tier-availability--low" : ""}`}>
                        <FaUsers />
                        <span>
                          {pack.packs_available <= 5
                            ? `Only ${pack.packs_available} bundle${pack.packs_available !== 1 ? "s" : ""} left!`
                            : `${pack.packs_available} bundles available`}
                        </span>
                      </div>
                    )}
                    {isSoldOut && (
                      <div className="tier-availability tier-availability--sold-out">
                        <span>Sold out</span>
                      </div>
                    )}
                  </div>

                  <div className="tier-right">
                    <div className="tier-price-group">
                      <span className="tier-price">{formatPrice(pack.pack_price_cents)}</span>
                      <span className="tier-pack-per-ticket">{formatPrice(perTicketCents)}/ticket</span>
                    </div>

                    {isOnSale && (
                      <div className="tier-quantity-stepper">
                        <button
                          className="stepper-btn"
                          onClick={() => onPackQuantityChange(pack.pack_id, Math.max(0, qty - 1))}
                          disabled={qty === 0}
                          aria-label="Decrease quantity"
                        >−</button>
                        <span className="stepper-count">{qty}</span>
                        <button
                          className="stepper-btn"
                          onClick={() => onPackQuantityChange(pack.pack_id, Math.min(maxQty, qty + 1))}
                          disabled={qty >= maxQty}
                          aria-label="Increase quantity"
                        >+</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
